import { cloneDeep } from 'lodash';
import {
  AngleModificationMode,
  MotionPoint,
  PartUtil,
  WorkPiecePosition,
} from '../../../../../../../util/part.util';
import { BeaconWeldingData } from '../../../model/beacon-welding-data';
import { MultipassComponentSettingsFormData } from './multipass.hooks';

/**
 * Generates motion points for a pass using the root pass points and schedule form data
 */
export const getMotionPointsFromScheduleForm = ({
  rootPassMotionPoints,
  form,
  angleMode,
  workpiecePosition,
}: {
  rootPassMotionPoints: MotionPoint[];
  form: BeaconWeldingData.MultipassPointOffsetFormData;
  angleMode: AngleModificationMode;
  workpiecePosition?: WorkPiecePosition;
}): MotionPoint[] => {
  // Deep clone inputs to avoid mutations
  const clonedPoints = cloneDeep(rootPassMotionPoints);
  const clonedForm = cloneDeep(form);

  return PartUtil.applyPointOffsets(
    clonedPoints,
    {
      alongTorchWire: clonedForm.stickoutZ,
      endAlongPath: clonedForm.endOffsetX,
      startAlongPath: clonedForm.startOffsetX,
      lateralToPath: clonedForm.perpendicularToPathY,
      mode: angleMode,
      travelAngle: clonedForm.travelAngle,
      workAngle: clonedForm.workAngle,
    },
    clonedForm.reversePath ? 'reverse' : 'forward',
    workpiecePosition
  );
};

/**
 * Strips out non-persisted fields from multipass form data that should not be saved
 * Removes context-specific fields and post actions to create reusable multipass schedule data
 */
export const stripNonPersistedMultipassScheduleFields = (
  values: MultipassComponentSettingsFormData
): Omit<
  MultipassComponentSettingsFormData,
  | 'reference'
  | 'rootPassOffsets'
  | 'startWeldSequence'
  | 'endWeldSequence'
  | 'rootPassPostActions'
> & {
  schedule?: Array<
    Omit<BeaconWeldingData.MultipassPointOffsetFormData, 'postActions'>
  >;
} => {
  const {
    rootPassOffsets,
    reference,
    startWeldSequence,
    endWeldSequence,
    rootPassPostActions,
    schedule,
    ...persistedValues
  } = values;

  // Strip post actions from individual passes
  const cleanedSchedule = schedule?.map((pass) => {
    const { postActions, ...cleanPass } = pass;
    return cleanPass;
  });

  return {
    ...persistedValues,
    schedule: cleanedSchedule,
  };
};
